import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { apiClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import BulkActionBar from '../components/BulkActionBar';
import { Button, Card, StatusPill, EmptyState, Skeleton, useToast } from '../../design-system';
import '../styles/ApprovalQueue.css';
import '../styles/BulkActions.css';
import { useLiveRefresh } from '../../lib/useLiveRefresh';
import { invalidateNavCounts } from '../../lib/navCounts';

const STATUS_CONFIG = {
  proposed: {
    title: 'Approval Queue',
    subtitle: 'System-proposed compliance tasks awaiting sign-off',
    emptyTitle: 'No proposed tasks right now',
    emptyDescription: 'Tasks appear here automatically as compliance due dates approach, or after running the task-proposer job.',
  },
  approved: {
    title: 'Approved Tasks',
    subtitle: 'Proposed tasks that have been signed off',
    emptyTitle: 'No approved tasks yet',
    emptyDescription: 'Tasks you approve from the queue will show up here.',
  },
  rejected: {
    title: 'Rejected Tasks',
    subtitle: 'Proposed tasks that were declined outright',
    emptyTitle: 'No rejected tasks',
    emptyDescription: 'Tasks you reject from the queue will show up here.',
  },
};

function ApprovalQueue({ status = 'proposed' }) {
  const revision = useLiveRefresh();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const navigate = useNavigate();
  const toast = useToast();
  const config = STATUS_CONFIG[status];

  useEffect(() => {
    fetchTasks();
    setSelectedIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, revision]);

  const toggleOne = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allSelected = tasks.length > 0 && selectedIds.length === tasks.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : tasks.map((t) => t._id));

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/api/tasks', { params: { approvalStatus: status } });
      setTasks(res.data);
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (taskId) => {
    setActingOn(taskId);
    try {
      await apiClient.patch(`/api/tasks/${taskId}/approve`);
      setTasks((prev) => prev.filter((t) => t._id !== taskId));
      // ⚠️ The sidebar's "Approvals" pill is a module-level cache shared by
      // every page, so it cannot see this row leave the list. Left alone it
      // goes on claiming the old number for its whole TTL — on the one screen
      // whose entire purpose is emptying it.
      invalidateNavCounts();
      toast.success('Task approved');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to approve task');
    } finally {
      setActingOn(null);
    }
  };

  const handleReject = async (taskId) => {
    if (!window.confirm('Reject this proposed task? It will not be created.')) return;
    setActingOn(taskId);
    try {
      await apiClient.patch(`/api/tasks/${taskId}/reject`);
      setTasks((prev) => prev.filter((t) => t._id !== taskId));
      // ⚠️ The sidebar's "Approvals" pill is a module-level cache shared by
      // every page, so it cannot see this row leave the list. Left alone it
      // goes on claiming the old number for its whole TTL — on the one screen
      // whose entire purpose is emptying it.
      invalidateNavCounts();
      toast.info('Task rejected');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to reject task');
    } finally {
      setActingOn(null);
    }
  };

  const handleReopen = async (taskId) => {
    setActingOn(taskId);
    try {
      await apiClient.patch(`/api/tasks/${taskId}/reopen`);
      setTasks((prev) => prev.filter((t) => t._id !== taskId));
      // ⚠️ The sidebar's "Approvals" pill is a module-level cache shared by
      // every page, so it cannot see this row leave the list. Left alone it
      // goes on claiming the old number for its whole TTL — on the one screen
      // whose entire purpose is emptying it.
      invalidateNavCounts();
      toast.success('Task moved back to pending');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to reopen task');
    } finally {
      setActingOn(null);
    }
  };

  const isOverdue = (task) => status === 'proposed' && task.dueDate && new Date(task.dueDate) < new Date();

  if (loading) {
    return (
      <OrgAdminLayout title={config.title} subtitle={config.subtitle}>
        <div className="queue-list">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}><Skeleton.Text lines={2} /></Card>
          ))}
        </div>
      </OrgAdminLayout>
    );
  }

  return (
    <OrgAdminLayout title={config.title} subtitle={config.subtitle}>
      <div className="approval-queue">
        {tasks.length === 0 ? (
          <EmptyState
            icon="📥"
            title={config.emptyTitle}
            description={config.emptyDescription}
          />
        ) : (
          <div className="queue-list">
            {/* Select-all + bulk bar. At quarter-end this queue holds hundreds
                of proposed tasks; approving them one at a time is the thing
                that makes it unusable. */}
            <div className="queue-selectall">
              <label>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                Select all {tasks.length}
              </label>
            </div>

            <BulkActionBar
              selectedIds={selectedIds}
              tasks={tasks}
              onClear={() => setSelectedIds([])}
              onDone={fetchTasks}
              canApprove={status === 'proposed'}
              canEdit
            />

            <AnimatePresence>
              {tasks.map((task) => (
                <motion.div
                  key={task._id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                >
                  <Card className={`queue-card ${isOverdue(task) ? 'overdue' : ''}`}>
                    <input
                      type="checkbox"
                      className="queue-check"
                      checked={selectedIds.includes(task._id)}
                      onChange={() => toggleOne(task._id)}
                      aria-label={`Select ${task.title}`}
                    />
                    <div className="queue-info">
                      <div className="queue-title-row">
                        <strong>{task.title}</strong>
                        <StatusPill status={task.priority} />
                      </div>
                      <div className="queue-meta">
                        {task.clientId?.displayName && (
                          <button
                            className="client-link"
                            onClick={() => navigate(`/org-admin/clients/${task.clientId._id}`)}
                          >
                            {task.clientId.displayName}
                          </button>
                        )}
                        {task.preparerId?.name && <span className="meta-text">Preparer: {task.preparerId.name}</span>}
                        {task.reviewerId?.name && <span className="meta-text">Reviewer: {task.reviewerId.name}</span>}
                        {task.dueDate && (
                          <span className={isOverdue(task) ? 'overdue-text' : 'meta-text'}>
                            Due: {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="queue-actions">
                      {status === 'proposed' ? (
                        <>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleApprove(task._id)}
                            disabled={actingOn === task._id}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleReject(task._id)}
                            disabled={actingOn === task._id}
                          >
                            Reject
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleReopen(task._id)}
                            disabled={actingOn === task._id}
                          >
                            Move to Pending
                          </Button>
                          <Button variant="secondary" size="sm" onClick={() => navigate(`/org-admin/tasks/${task._id}`)}>
                            View Task
                          </Button>
                        </>
                      )}
                    </div>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </OrgAdminLayout>
  );
}

export default ApprovalQueue;
